// Complete-the-set builder. Locked behaviors:
// - the WHOLE card toggles (delegated on the row, fixed card stays locked)
// - rail minis lift INTO the set row as ticked cards and back out again
//   (grid tracks follow via --setcols); the mini itself stays a product link,
//   only its "+" button promotes it
// - "Add set to cart" posts every ticked line to /cart/add.js in one items[]
//   call and dispatches Horizon's CartLinesUpdateEvent so the drawer reacts.
// - every line price (data-catalog + campaign data-pct/-fixed) is computed
//   through the A/B tested price, so the set total follows the visitor's arm.
import { CartLinesUpdateEvent } from '@shopify/events';
import { preorderPropsFromDataset } from '@theme/lettoria-preorder-property-builder';
import { testedPrice, campaignPrice, money, repriceTested, onTestedPrices } from '@theme/lettoria-tested-price';

class LettoriaSetBuilder extends HTMLElement {
  connectedCallback() {
    this.row = this.querySelector('.set__row');
    this.sum = this.querySelector('.set__sum');
    this.totalEl = this.querySelector('[ref-set-total]');
    this.countEl = this.querySelector('[ref-set-count]');
    this.addButton = this.querySelector('[ref-set-add]');

    this.row?.addEventListener('click', (event) => {
      const card = event.target.closest('.set__item');
      if (card && card.querySelector('.set__check')) {
        card.classList.toggle('is-off');
        this.recompute();
      }
    });

    for (const mini of this.querySelectorAll('.set__mini')) {
      mini.querySelector('.plus')?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleMini(mini);
      });
    }

    this.addButton?.addEventListener('click', () => this.addSet());
    this.recompute();
    this.unsubscribePrices = onTestedPrices(() => {
      repriceTested(this);
      this.recompute();
    });
  }

  disconnectedCallback() {
    this.unsubscribePrices?.();
  }

  /** Line price in cents: campaign maths over the tested catalog price. */
  linePrice(card) {
    const catalog = parseInt(card.getAttribute('data-catalog') ?? '', 10);
    if (!Number.isFinite(catalog)) return 0;
    const tested = testedPrice(card.getAttribute('data-variant-id'), catalog);
    return campaignPrice(tested, parseInt(card.getAttribute('data-pct'), 10) || 0, parseInt(card.getAttribute('data-fixed'), 10) || 0);
  }

  toggleMini(mini) {
    const id = `mini-${mini.getAttribute('data-variant-id')}`;
    const plus = mini.querySelector('.plus');
    const existing = this.row.querySelectorAll(`[data-from="${id}"]`);
    if (existing.length) {
      existing.forEach((el) => el.remove());
      mini.classList.remove('is-in');
      if (plus) plus.textContent = '+';
      this.recompute();
      return;
    }
    const sep = document.createElement('div');
    sep.className = 'set__plus';
    sep.textContent = '+';
    sep.setAttribute('data-from', id);
    const card = document.createElement('div');
    card.className = 'set__item';
    for (const priceAttr of ['data-catalog', 'data-pct', 'data-fixed']) {
      card.setAttribute(priceAttr, mini.getAttribute(priceAttr) ?? '');
    }
    card.setAttribute('data-variant-id', mini.getAttribute('data-variant-id'));
    // Pre-order promise travels with the promoted card so addSet can post it.
    for (const poAttr of ['data-po-batch', 'data-po-eta']) {
      const poValue = mini.getAttribute(poAttr);
      if (poValue) card.setAttribute(poAttr, poValue);
    }
    card.setAttribute('data-from', id);
    // Promoted cards render at full set-item size, so use the crisp 480px source
    // (data-img) instead of the 44px rail thumbnail, which upscales blurry.
    const cardImg = mini.getAttribute('data-img') || mini.querySelector('img')?.getAttribute('src') || '';
    card.innerHTML = `<span class="set__check">✓</span><img src="${cardImg}" alt="">`
      + `<div class="set__name">${mini.querySelector('.n')?.textContent ?? ''}</div>`
      + `<div class="set__price">${mini.querySelector('.p')?.innerHTML ?? ''}</div>`;
    this.row.insertBefore(sep, this.sum);
    this.row.insertBefore(card, this.sum);
    mini.classList.add('is-in');
    if (plus) plus.textContent = '−';
    this.recompute();
  }

  activeCards() {
    return Array.from(this.row.querySelectorAll('.set__item')).filter(
      (card) => card.getAttribute('data-fixed') === '1' || !card.classList.contains('is-off')
    );
  }

  recompute() {
    const cards = this.activeCards();
    let total = 0;
    for (const card of cards) total += this.linePrice(card);
    if (this.totalEl) this.totalEl.textContent = money(total);
    if (this.countEl) this.countEl.textContent = `${cards.length} item${cards.length === 1 ? '' : 's'}`;

    const allCards = this.row.querySelectorAll('.set__item').length;
    const cols = Array(allCards).fill('1fr').join(' auto ');
    this.row.style.setProperty('--setcols', cols);
  }

  async addSet() {
    const lines = this.activeCards()
      .map((card) => {
        const line = { id: parseInt(card.getAttribute('data-variant-id'), 10), quantity: 1 };
        // Armed pre-order line: the promise properties ride along (app
        // contract — every /cart/add for an armed variant must carry them).
        const poProps = preorderPropsFromDataset(card);
        if (poProps) line.properties = poProps;
        return line;
      })
      .filter((line) => Number.isFinite(line.id));
    if (!lines.length) return;

    this.addButton.setAttribute('disabled', '');
    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines }),
      });
      if (!response.ok) throw new Error(`cart add HTTP ${response.status}`);

      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const ajaxCart = await cartResponse.json();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
          promise: Promise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: { items: ajaxCart.items, source: 'lettoria-set-builder', sourceId: this.id || 'set-builder' },
          }),
        })
      );
      this.addButton.textContent = 'Added ✓';
      setTimeout(() => {
        this.addButton.textContent = 'Add set to cart';
        this.addButton.removeAttribute('disabled');
      }, 1800);
    } catch (error) {
      console.error('set add failed', error);
      this.addButton.removeAttribute('disabled');
    }
  }
}

if (!customElements.get('lettoria-set-builder')) {
  customElements.define('lettoria-set-builder', LettoriaSetBuilder);
}
